export function SellMyApartmentAsIs() {
  return (
    <section className="py-20 bg-gray-50" id="sell-as-is">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Sell My Apartment As-Is - No Repairs, No Hassles
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Skip costly repairs and renovations. Learn how to sell your apartment building in its current condition 
            and still get a fair price from buyers who specialize in fixer-upper properties.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/67855ef4523414d9c57de625ff5ba135.jpg"
              alt="Apartment building in as-is condition"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Benefits of Selling As-Is</h3>
            <p className="text-gray-600 mb-8">
              Selling your apartment building as-is can save you significant time, money, and stress while still 
              achieving a fair market price from the right buyer.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Save Repair Costs</h4>
                  <p className="text-gray-600">
                    Avoid spending $50,000-$500,000+ on repairs and renovations before selling. 
                    Let the buyer handle improvements with their own contractors and vision.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Faster Timeline</h4>
                  <p className="text-gray-600">
                    Sell immediately without spending 3-12 months on repairs, permits, and renovations. 
                    Get to market quickly and start receiving offers.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">No Risk of Over-Improving</h4>
                  <p className="text-gray-600">
                    Avoid the risk of spending more on improvements than you'll recoup in sale price. 
                    Professional investors know exactly what improvements add value.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-stress-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Eliminate Contractor Stress</h4>
                  <p className="text-gray-600">
                    No dealing with contractors, permits, inspections, or construction delays. 
                    Transfer the property in its current state and walk away stress-free.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Common As-Is Property Conditions We Buy
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-tools-line text-3xl text-red-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Deferred Maintenance</h4>
              <p className="text-gray-600">
                Properties with years of deferred maintenance including HVAC issues, plumbing problems, 
                roof repairs, and electrical updates needed.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-paint-brush-line text-3xl text-orange-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Cosmetic Updates Needed</h4>
              <p className="text-gray-600">
                Buildings requiring new flooring, paint, fixtures, appliances, and general aesthetic 
                improvements to attract modern tenants.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-fire-line text-3xl text-blue-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Fire or Water Damage</h4>
              <p className="text-gray-600">
                Properties with damage from fires, floods, storms, or other disasters requiring 
                extensive restoration work.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-bug-line text-3xl text-purple-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Pest or Mold Issues</h4>
              <p className="text-gray-600">
                Buildings with pest infestations, mold problems, or other environmental issues 
                requiring professional remediation.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-building-line text-3xl text-green-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Code Violations</h4>
              <p className="text-gray-600">
                Properties with city code violations, safety issues, or required upgrades to meet 
                current building standards and regulations.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-ancient-gate-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Outdated Systems</h4>
              <p className="text-gray-600">
                Buildings with outdated electrical, plumbing, HVAC systems that need complete 
                replacement or major upgrades.
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">How As-Is Pricing Works</h3>
            <p className="text-gray-600 mb-8">
              As-is pricing considers the property's current condition, required repairs, and market value 
              after renovations. Understanding this helps set realistic expectations.
            </p>
            
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
              <h4 className="text-lg font-semibold text-blue-800 mb-3">Typical As-Is Formula:</h4>
              <div className="space-y-2 text-gray-700">
                <div className="flex justify-between items-center">
                  <span>After Repair Value (ARV)</span>
                  <span className="font-semibold">$1,000,000</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Estimated Repair Costs</span>
                  <span className="font-semibold text-red-600">- $200,000</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Buyer's Profit Margin (15-25%)</span>
                  <span className="font-semibold text-red-600">- $150,000</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Carrying Costs & Fees</span>
                  <span className="font-semibold text-red-600">- $50,000</span>
                </div>
                <hr className="border-gray-300" />
                <div className="flex justify-between items-center font-bold text-lg">
                  <span>As-Is Offer Price</span>
                  <span className="text-green-600">$600,000</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
                <p className="text-sm text-yellow-800 font-medium mb-1">Important Note:</p>
                <p className="text-sm text-gray-700">
                  While as-is offers may seem low, they represent immediate cash with no repair costs, 
                  time investment, or sale uncertainties.
                </p>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=Professional%20contractor%20with%20clipboard%20estimating%20renovation%20costs%20for%20apartment%20building%20with%20calculator%20and%20repair%20cost%20sheets%20realistic%20assessment%20scene&width=800&height=600&seq=sell-apt-estimate-11&orientation=landscape"
              alt="Contractor estimating renovation costs"
              className="w-full h-96 object-cover rounded-xl shadow-lg mb-6"
            />
            
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-green-800 mb-3">
                <i className="ri-calculator-line mr-2"></i>
                Free Repair Cost Analysis
              </h4>
              <p className="text-gray-700 mb-4">
                We provide detailed repair cost estimates so you understand exactly how we arrive 
                at our offer price. Full transparency in our evaluation process.
              </p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Structural assessments</li>
                <li>• Mechanical system evaluations</li>
                <li>• Cosmetic improvement costs</li>
                <li>• Code compliance requirements</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-12 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            As-Is Sale Process - Simple & Straightforward
          </h3>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Property Evaluation</h4>
              <p className="text-gray-600">
                We assess your property's current condition and potential value after improvements. 
                No preparation needed from you.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Fair Cash Offer</h4>
              <p className="text-gray-600">
                Receive a detailed cash offer with transparent repair cost estimates and 
                our evaluation methodology.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Accept & Sign</h4>
              <p className="text-gray-600">
                Review our offer and contract terms. No contingencies for repairs, 
                inspections, or property improvements.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">4</span>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Close & Get Paid</h4>
              <p className="text-gray-600">
                Close in 7-30 days and receive your cash payment. We handle all repairs 
                and improvements after closing.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-teal-500 to-cyan-600 rounded-xl p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">
            We Buy Properties in ANY Condition
          </h3>
          <p className="text-xl mb-8 max-w-4xl mx-auto">
            Don't spend a dime on repairs. We specialize in purchasing apartment buildings as-is and 
            have the expertise and capital to handle any renovation challenges.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-3xl font-bold mb-2">$0</div>
              <div className="text-lg">Repair Costs for You</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-3xl font-bold mb-2">Any</div>
              <div className="text-lg">Property Condition</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-3xl font-bold mb-2">Fair</div>
              <div className="text-lg">As-Is Pricing</div>
            </div>
          </div>
          <button className="bg-white text-teal-600 px-12 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 whitespace-nowrap">
            Get As-Is Property Offer
          </button>
        </div>
      </div>
    </section>
  );
}